import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { IsActiveMatchOptions, NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { FuseUtilsService } from '@fuse/services/utils/utils.service';
import { ApiService } from 'app/core/services/api.service';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';

@Component({
    selector: 'fuse-vertical-navigation-basic-item',
    templateUrl: './basic.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [],
})
export class FuseVerticalNavigationBasicItemComponent
    implements OnInit, OnDestroy
{
    @Input() item: FuseNavigationItem;
    @Input() name: string;

    isActiveMatchOptions: IsActiveMatchOptions;
    private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    defaultSavedView: any;
    livechatUnreadMsg: number = 0;
    lgParam: boolean;
    activeParam: any;
    iconPathSrc: any;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseUtilsService: FuseUtilsService,
        private _router: Router,
        private api: ApiService,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private _fuseMediaWatcherService: FuseMediaWatcherService
    ) {
        // Set the equivalent of {exact: false} as default for active match options.
        // We are not assigning the item.isActiveMatchOptions directly to the
        // [routerLinkActiveOptions] because if it's "undefined" initially, the router
        // will throw an error and stop working.
        this.isActiveMatchOptions = this._fuseUtilsService.subsetMatchOptions;
        this.iconPathSrc = '/assets/icons/sidebar/';

        this.matIconRegistry
            .addSvgIcon(
                'announced',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}announced.svg`
                )
            )
            .addSvgIcon(
                'whatsapp_broadcast',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}whatsapp_broadcast.svg`
                )
            )
            .addSvgIcon(
                'blank_duotone',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}Blank_duotone_line.svg`
                )
            )
            .addSvgIcon(
                'creadit_card',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}creadit_card.svg`
                )
            )
            .addSvgIcon(
                'settings',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}settings.svg`
                )
            )
            .addSvgIcon(
                'warmup',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}mail-star-fill.svg`
                )
            )
            .addSvgIcon(
                'templates',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}template.svg`
                )
            )
            .addSvgIcon(
                'integration',
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `${this.iconPathSrc}integration.svg`
                )
            );

        this._router.events.subscribe((event: any) => {
            // if (event.routerEvent == NavigationEnd) {
            //     console.log("navigation event", event)
            // }
            if (event.routerEvent instanceof NavigationEnd) {
                this.activeParam = event.routerEvent.url;
                // if (param && param.id) {
                //     this.activeParam == param.id
                // }
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the "isActiveMatchOptions" either from item's
        // "isActiveMatchOptions" or the equivalent form of
        // item's "exactMatch" option
        this.isActiveMatchOptions =
            this.item.isActiveMatchOptions ?? this.item.exactMatch
                ? this._fuseUtilsService.exactMatchOptions
                : this._fuseUtilsService.subsetMatchOptions;

        // Get the parent navigation component
        this._fuseVerticalNavigationComponent =
            this._fuseNavigationService.getComponent(this.name);

        // Mark for check
        this.api.isLoadSettings
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this._changeDetectorRef.markForCheck();
            });
        this._changeDetectorRef.markForCheck();

        // Subscribe to onRefreshed on the navigation component
        this._fuseVerticalNavigationComponent.onRefreshed
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this.api.isLoadSettings
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                if (res == true) {
                    this.defaultSavedView = this.api.settingsData.default_view
                        ? this.api.settingsData.default_view != 'saved_view'
                            ? this.api.settingsData.default_view
                            : this.api.settingsData.saved_view_id
                        : 'all';
                }
            });

        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Set the drawerMode and drawerOpened if
                if (matchingAliases.includes('lg')) {
                    this.lgParam = false;
                } else {
                    this.lgParam = true;
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
